import React, { useState, useCallback } from "react";

import { ActionsMenu, MenuItem, ActionMenuDivider } from "ui/actions-menu";
import toast from "components/partials/toast/toast";
import { IconButton } from "ui/icon-button";
import { useHandleEditorOpen } from "containers/admin/clients/touchpoint/components/builder/use-handle-editor-open";

import type { EmailAttributes } from "hooks/api/use-touchpoint-versions";

import { useClaimVersionLock } from "hooks/api/use-touchpoint-versions";
import { getTouchpointVersion } from "generated/api/touchpoint-version";
import { mapTouchpointVersionDTO } from "utilities/api-mappers";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import { useCurrentUser } from "hooks/api/use-current-user";

const touchpointVersionApi = getTouchpointVersion();

import { isLocked, getActionTouchpointTypeLabel } from "utilities";

import { TouchpointVersionsTableActionsMenuProps } from "containers/admin/clients/touchpoint/tab-components/version-history-tab/components/touchpoint-versions-table-actions-menu/touchpoint-versions-table-actions-menu.types";
import { TouchpointType } from "types/touchpoint";

import { PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";
import Icons from "assets";

const TouchpointVersionsTableActionsMenu: React.FC<TouchpointVersionsTableActionsMenuProps> = ({
  item,
  version,
  versionIsPublished,
  isEditable,
  currentTouchpointVersionType,
  isStudentPopulation,
  permissionToPublish,
  // Modal controls from parent
  onSetCurrentActionItem,
  onSetSelectedVersion,
  onOpenConfirmArchiveModal,
  onOpenConfirmUpdateMergeTagsModal,
  isUpdatingMergeTags,
  onOpenCloneTouchpointModal,
  onOpenCloneTemplateModal,
  onOpenConfirmPublishModal,
  onOpenEditVersionModal,
  onOpenNewDraftModal,
  onOpenCreateParentTouchpointModal,
  onOpenUpdateClientThemeModal,
}) => {
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();
  const currentUser = useCurrentUser();
  const isTemplate = currentCampaign?.name === PROGRAM_TEMPLATES_CAMPAIGN_NAME;
  const touchpointIsOther = currentTouchpoint?.type === TouchpointType.OTHER;
  const [isOpen, setIsOpen] = useState(false);
  const [isLockedByAnotherUser, setIsLockedByAnotherUser] = useState(
    version ? isLocked(version as any, currentUser) : false,
  );

  const handleMenuOpen = () => setIsOpen(true);
  const handleMenuClose = () => setIsOpen(false);

  const claimLockMutation = useClaimVersionLock({
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
  });

  const { handleEditorOpen } = useHandleEditorOpen({
    selectedVersion: version,
    latestVersion: version,
  });

  const verifyLockAndProceed = useCallback(
    async (callback: () => void) => {
      try {
        if (!version) return;

        const freshVersionDto = await touchpointVersionApi.getOneVersion({
          clientId: currentClient?.id,
          campaignId: currentCampaign?.id,
          touchpointId: currentTouchpoint?.id,
          id: version.id,
        });
        const freshVersion = mapTouchpointVersionDTO(freshVersionDto);

        const isLockedByAnother = isLocked(freshVersion as any, currentUser);

        setIsLockedByAnotherUser(isLockedByAnother);

        if (isLockedByAnother) {
          toast.warning({
            data: {
              title: "Action not permitted",
              message: `This version is currently locked by ${freshVersion.lockedBy?.firstName} ${freshVersion.lockedBy?.lastName}`,
            },
          });
        } else {
          // Not locked, proceed with the action
          callback();
        }
      } catch (error) {
        console.error("Error verifying lock status:", error);
        toast.error({
          data: {
            title: "Failed to verify lock status",
          },
        });
      }
    },
    [
      version,
      currentClient,
      currentCampaign,
      currentTouchpoint,
      currentUser,
      setIsLockedByAnotherUser,
    ],
  );

  const handleEditVersionDetails = useCallback(() => {
    verifyLockAndProceed(() => {
      claimLockMutation.mutate(version.id);
      onSetCurrentActionItem(version);
      onOpenEditVersionModal();
    });
  }, [
    onSetCurrentActionItem,
    onOpenEditVersionModal,
    version,
    verifyLockAndProceed,
    claimLockMutation,
  ]);

  const handleArchiveDraft = useCallback(() => {
    verifyLockAndProceed(() => {
      onSetCurrentActionItem(version);
      onOpenConfirmArchiveModal();
    });
  }, [onSetCurrentActionItem, onOpenConfirmArchiveModal, version, verifyLockAndProceed]);

  const handlePublishToIterable = useCallback(() => {
    verifyLockAndProceed(() => {
      onSetCurrentActionItem(version);
      onOpenConfirmPublishModal();
    });
  }, [onSetCurrentActionItem, onOpenConfirmPublishModal, version, verifyLockAndProceed]);

  const handleCreateDraftFromVersion = useCallback(() => {
    verifyLockAndProceed(() => {
      onSetCurrentActionItem(version);
      onOpenNewDraftModal();
    });
  }, [onSetCurrentActionItem, onOpenNewDraftModal, version, verifyLockAndProceed]);

  const handleCloneTouchpoint = useCallback(() => {
    verifyLockAndProceed(() => {
      onSetSelectedVersion(version);
      onOpenCloneTouchpointModal();
    });
  }, [onSetSelectedVersion, onOpenCloneTouchpointModal, version, verifyLockAndProceed]);

  const handleCloneTemplate = useCallback(() => {
    verifyLockAndProceed(() => {
      onSetSelectedVersion(version);
      onOpenCloneTemplateModal();
    });
  }, [onSetSelectedVersion, onOpenCloneTemplateModal, version, verifyLockAndProceed]);

  const handleUpdateMergeTags = useCallback(() => {
    verifyLockAndProceed(() => {
      onSetCurrentActionItem(version);
      onOpenConfirmUpdateMergeTagsModal();
    });
  }, [version, verifyLockAndProceed, onSetCurrentActionItem, onOpenConfirmUpdateMergeTagsModal]);

  const handleCreateParentTouchpoint = useCallback(() => {
    verifyLockAndProceed(() => {
      onSetSelectedVersion(version);
      onOpenCreateParentTouchpointModal();
    });
  }, [onSetSelectedVersion, onOpenCreateParentTouchpointModal, version, verifyLockAndProceed]);

  const handleOpenContentSettings = useCallback(() => {
    verifyLockAndProceed(() => handleEditorOpen("content-settings"));
  }, [verifyLockAndProceed, handleEditorOpen]);

  const handleOpenUpdateClientThemeModal = useCallback(() => {
    verifyLockAndProceed(() => {
      onSetSelectedVersion(version);
      onOpenUpdateClientThemeModal();
    });
  }, [onSetSelectedVersion, onOpenUpdateClientThemeModal, version, verifyLockAndProceed]);

  const isEmail = currentTouchpointVersionType === "Email";
  const canCreateParentTouchpoint =
    isEmail && isStudentPopulation && !(version as EmailAttributes).emptyContent;

  const canApplyTheme =
    isEmail && !(version as EmailAttributes).emptyContent && (version as EmailAttributes).applyClientTheme === true;

  const getTooltip = () => {
    if (isLockedByAnotherUser) {
      return {
        message: version?.lockedBy
          ? `This version is currently locked by ${version.lockedBy.firstName} ${version.lockedBy.lastName}`
          : "Disabled",
        icon: <Icons.LockClosed className="size-4 text-gray-600" />,
      };
    }

    if (!version?.publishable && !touchpointIsOther) {
      return {
        message: "Invalid or blank fields in Content settings must be corrected before publishing",
        icon: (
          <IconButton
            variant="text"
            color="error"
            size="large"
            aria-label=""
            onClick={handleOpenContentSettings}>
            <Icons.ExclamationCircle />
          </IconButton>
        ),
      };
    }
    return undefined;
  };

  const renderCreateDraftItem = () => (
    <MenuItem
      icon={<Icons.NewDocument />}
      label="Create draft from this version"
      onClick={handleCreateDraftFromVersion}
      disabled={isLockedByAnotherUser}
      tooltip={getTooltip()}
    />
  );

  const renderCloneItem = () => (
    <MenuItem
      icon={<Icons.Clone />}
      label={
        isTemplate
          ? "Clone template"
          : `Clone ${getActionTouchpointTypeLabel(currentTouchpoint.type).toLowerCase()}`
      }
      onClick={isTemplate ? handleCloneTemplate : handleCloneTouchpoint}
      disabled={isLockedByAnotherUser}
      tooltip={getTooltip()}
    />
  );

  const renderApplyThemeItem = () => {
    if (!canApplyTheme) return null;

    return (
      <MenuItem
        icon={<Icons.Theme />}
        label="Apply theme"
        onClick={handleOpenUpdateClientThemeModal}
        disabled={isLockedByAnotherUser}
        tooltip={getTooltip()}
      />
    );
  };

  const renderCreateParentTouchpointItem = () => {
    if (!canCreateParentTouchpoint) return null;

    return (
      <MenuItem
        icon={<Icons.Parent />}
        label="Create parent email"
        onClick={handleCreateParentTouchpoint}
        disabled={isLockedByAnotherUser}
        tooltip={getTooltip()}
      />
    );
  };

  const renderUpdateMergeTagsItem = () => {
    if (!isEmail || (version as EmailAttributes).emptyContent) return null;

    return (
      <MenuItem
        icon={<Icons.UpdateMergeTags />}
        label="Update merge tags"
        onClick={handleUpdateMergeTags}
        disabled={isLockedByAnotherUser || isUpdatingMergeTags}
        tooltip={getTooltip()}
      />
    );
  };

  const renderEditableActions = () => {
    if (!isEditable) return null;

    return (
      <>
        <ActionMenuDivider />

        <MenuItem
          icon={<Icons.Document />}
          label="Edit version details"
          onClick={handleEditVersionDetails}
          disabled={isLockedByAnotherUser}
          tooltip={getTooltip()}
        />

        <MenuItem
          icon={<Icons.Archive />}
          label="Archive draft"
          color="error"
          onClick={handleArchiveDraft}
          disabled={isLockedByAnotherUser}
          tooltip={getTooltip()}
        />
      </>
    );
  };

  const renderPublishAction = () => {
    const mergeTagsItem = renderUpdateMergeTagsItem();
    const publishItem =
      permissionToPublish && !isTemplate && !touchpointIsOther ? (
        <MenuItem
          icon={<Icons.Upload />}
          label={isEmail ? "Publish to Iterable" : "Publish"}
          onClick={handlePublishToIterable}
          disabled={isLockedByAnotherUser || !version?.publishable}
          tooltip={getTooltip()}
        />
      ) : null;

    if (!mergeTagsItem && !publishItem) return null;

    return (
      <>
        <ActionMenuDivider />
        {mergeTagsItem}
        {publishItem}
      </>
    );
  };

  const renderPublishedVersionItems = () => (
    <>
      {renderCreateDraftItem()}
      {renderCloneItem()}
      {renderCreateParentTouchpointItem()}
      {renderUpdateMergeTagsItem()}
    </>
  );

  const renderUnpublishedVersionItems = () => (
    <>
      {renderCreateDraftItem()}
      {renderApplyThemeItem()}
      {renderCloneItem()}
      {renderCreateParentTouchpointItem()}
      {renderEditableActions()}
      {renderPublishAction()}
    </>
  );

  return (
    <ActionsMenu
      open={isOpen}
      onOpen={handleMenuOpen}
      onClose={handleMenuClose}
      itemName={item.itemName}
      menuId={`touchpoint-version-actions-menu-${item.itemId}`}>
      {versionIsPublished ? renderPublishedVersionItems() : renderUnpublishedVersionItems()}
    </ActionsMenu>
  );
};

export default TouchpointVersionsTableActionsMenu;
